import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import { styleMap } from 'lit/directives/style-map.js';

import { BaseElement } from '../base-element.ts';
import leftArrowUrl from './assets/arrow-left.svg';
import rightArrowUrl from './assets/arrow-right.svg';
import type { SliderItem } from './types.ts';

@customElement('dur-slider')
export class DurSlider extends BaseElement {
  @property({ type: Array }) items: SliderItem[] = [];
  @property({ type: Number }) autoplay = 0;
  @property({ type: Number }) parallaxFactor = 0.15;
  @property({ type: String }) align: 'center' | 'left' = 'center';
  @state() private _currentIndex = 0;
  @state() private _isDragging = false;
  @state() private _dragOffset = 0;
  private _dragStartX = 0;
  private _dragStartY = 0;
  private _wasDragged = false;

  private _autoplayTimerId?: number;

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has('_currentIndex')) {
      const allVideos = this.shadowRoot?.querySelectorAll('video');
      const currentSlideElement = this.shadowRoot?.querySelector(
        `.slide[data-index="${this._currentIndex}"]`
      );

      allVideos?.forEach((video) => {
        if (video.closest('.slide') !== currentSlideElement) {
          video.pause();
          video.currentTime = 0;
        }
      });

      const item = this.items[this._currentIndex];
      if (item?.type === 'video') {
        this._stopAutoplay();
        const video = currentSlideElement?.querySelector('video');
        if (video) {
          video.play().catch((e) => console.error('Autoplay falló:', e));
          video.onended = () => {
            this._nextSlide();
          };
        }
      } else {
        this._resetAutoplay();
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this._startAutoplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stopAutoplay();
  }

  private _startAutoplay() {
    this._stopAutoplay();
    if (this.autoplay > 0) {
      this._autoplayTimerId = window.setInterval(() => {
        this._nextSlide();
      }, this.autoplay);
    }
  }

  private _stopAutoplay() {
    if (this._autoplayTimerId) {
      clearInterval(this._autoplayTimerId);
      this._autoplayTimerId = undefined;
    }
  }

  private _resetAutoplay() {
    if (this.autoplay > 0) {
      this._startAutoplay();
    }
  }

  private _prevSlide() {
    this._currentIndex =
      (this._currentIndex - 1 + this.items.length) % this.items.length;
    this._resetAutoplay();
  }

  private _nextSlide() {
    this._currentIndex = (this._currentIndex + 1) % this.items.length;
    this._resetAutoplay();
  }

  private _onPointerDown(e: MouseEvent | TouchEvent) {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : 0;
    this._isDragging = true;
    this._dragStartX = clientX;
    this._dragStartY = clientY;
    this._dragOffset = 0;
    this._wasDragged = false;
    this._stopAutoplay();
  }

  private _onPointerMove(e: MouseEvent | TouchEvent) {
    if (!this._isDragging) return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : 0;
    const deltaX = clientX - this._dragStartX;
    const deltaY = clientY - this._dragStartY;

    if ('touches' in e && Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 10) {
      this._isDragging = false;
      this._resetAutoplay();
      return;
    }

    if ('touches' in e && Math.abs(deltaX) > 5) {
      e.preventDefault();
    }

    const containerWidth = this.shadowRoot?.querySelector('.slide')?.clientWidth ?? 1;
    let dragOffset = (deltaX / containerWidth) * 100;
    if (dragOffset > 100) dragOffset = 100;
    if (dragOffset < -100) dragOffset = -100;
    this._dragOffset = dragOffset;

    if (Math.abs(deltaX) > 5) {
      this._wasDragged = true;
    }
  }

  private _onPointerUp() {
    if (!this._isDragging) return;
    this._isDragging = false;

    const threshold = 20;
    if (this._dragOffset < -threshold) {
      this._nextSlide();
    } else if (this._dragOffset > threshold) {
      this._prevSlide();
    } else {
      this._resetAutoplay();
    }

    this._dragOffset = 0;

    if (this._wasDragged) {
      const handler = (e: Event) => {
        e.stopPropagation();
        e.preventDefault();
      };
      document.addEventListener('click', handler, { capture: true, once: true });
    }
  }

  private static readonly _breakpointMap = {
    tablet: '(min-width: 768px)',
    desktop: '(min-width: 992px)',
  };

  private _onMediaLoad(e: Event) {
    const mediaElement = e.target as HTMLElement;

    const slideContainer = mediaElement.closest('.slide');

    if (slideContainer) {
      slideContainer.querySelector('.spinner')?.classList.add('opacity-0');
    }

    mediaElement.classList.remove('opacity-0');
  }

  private _renderSlideContent(item: SliderItem, index: number) {
    const spinner = html`
      <div
        class="spinner absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity duration-300 z-10"
      >
        <svg
          class="animate-spin h-8 w-8 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    `;

    let mediaElement;
    if (item.type === 'image') {
      const picture = html`
        <picture>
          ${map(
            item.sources,
            (source) =>
              html`<source
                media=${DurSlider._breakpointMap[source.device]}
                srcset=${source.srcset}
              />`
          )}
          <img
            src=${item.defaultSrc}
            alt=${item.alt ?? ''}
            loading="lazy"
            class="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-500"
            @load=${this._onMediaLoad}
            @error=${this._onMediaLoad}
          />
        </picture>
      `;
      mediaElement = item.link
        ? html`<a href=${item.link} class="block w-full h-full">${picture}</a>`
        : picture;
    } else {
      mediaElement = html`<video
        src=${item.src}
        preload="metadata"
        muted
        playsinline
        class="w-full h-full object-cover opacity-0 transition-opacity duration-500"
        @loadeddata=${this._onMediaLoad}
        @error=${this._onMediaLoad}
      ></video>`;
    }

    if (this.parallaxFactor > 0) {
      const visualOffset =
        (index - this._currentIndex) * 100 + this._dragOffset;
      const parallaxOffset = -visualOffset * (1 - this.parallaxFactor);
      const parallaxStyle: Record<string, string | undefined> = {
        transform: `translateX(${parallaxOffset}%)`,
        transitionDuration: this._isDragging ? '0s' : undefined,
      };
      return html`
        <div
          class="relative w-full h-full transition-transform duration-500 ease-in-out"
          style=${styleMap(parallaxStyle)}
        >
          ${spinner}${mediaElement}
        </div>
      `;
    }

    return html`${spinner}${mediaElement}`;
  }

  private _renderSlideOverlay(item: SliderItem) {
    if (!item.title && !item.subtitle && !item.button) return '';

    const overlayClass = this.align === 'left' ? 'slide-overlay left' : 'slide-overlay';

    const titleStyle = item.subtitle
      ? 'margin-bottom: var(--gap-ts)'
      : item.button
        ? 'margin-bottom: var(--gap-sb)'
        : '';

    const subtitleStyle = item.button ? 'margin-bottom: var(--gap-sb)' : '';

    return html`
      <div class=${overlayClass}>
        <div class="slide-overlay-inner">
          ${item.title
            ? html`<span class="slide-title" style=${titleStyle}>${item.title}</span>`
            : ''}
          ${item.subtitle
            ? html`<span class="slide-subtitle" style=${subtitleStyle}>${item.subtitle}</span>`
            : ''}
          ${item.button
            ? html`<a class="slide-button" href=${item.button.url}>${item.button.text}</a>`
            : ''}
        </div>
      </div>
    `;
  }

  render() {
    if (!this.items?.length) {
      return html`<div>There are no items to display.</div>`;
    }

    const slideContainerStyles: Record<string, string | undefined> = {
      transform: `translateX(calc(-${this._currentIndex * 100}% + ${this._dragOffset}%))`,
      transitionDuration: this._isDragging ? '0s' : undefined,
      cursor: this._isDragging ? 'grabbing' : 'grab',
    };

    return html`
      <style>
        .slide-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-end;
          pointer-events: none;
          z-index: 5;
          padding-bottom: 9px;
          --gap-ts: 2px;
          --gap-sb: 13.5px;
        }
        .slide-overlay-inner {
          width: 100%;
          max-width: none;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .slide-overlay.left .slide-overlay-inner {
          align-items: flex-start;
        }
        .slide-overlay.left {
          align-items: flex-start;
          justify-content: center;
          padding-bottom: 0;
          padding-left: 9px;
        }
        .slide-overlay.left .slide-button {
          align-self: flex-start;
        }
        .slide-overlay a {
          pointer-events: auto;
        }

        .slide-title {
          color: #FFF;
          text-align: center;
          text-shadow: 0 0 34.127px #000;
          font-family: Mango;
          font-size: 40px;
          font-style: normal;
          font-weight: 400;
          line-height: 42px;
        }
        .slide-overlay.left .slide-title {
          text-align: left;
        }

        .slide-subtitle {
          color: #FFF;
          text-align: center;
          text-shadow: 0 0 17.063px #000;
          font-family: Lato;
          font-size: 8px;
          font-style: normal;
          font-weight: 500;
          line-height: normal;
          letter-spacing: 0.12px;
        }
        .slide-overlay.left .slide-subtitle {
          text-align: left;
        }

        .slide-button {
          display: flex;
          padding: 4.55px 6.825px;
          justify-content: center;
          align-items: center;
          border-radius: 28.439px;
          border: 0.284px solid #575756;
          background: #F9F9F7;
          color: #575756;
          font-family: Lato;
          font-size: 4.55px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;
          letter-spacing: 0.455px;
          text-transform: uppercase;
          text-decoration: none;
        }

        .nav-arrows {
          position: absolute;
          right: 24px;
          bottom: 24px;
          display: flex;
          gap: 24px;
        }
        .nav-arrows button {
          padding: 0;
          border: none;
          background: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: opacity 0.2s;
        }
        .nav-arrows button:hover {
          opacity: 0.7;
        }
        .nav-arrows img {
          width: 43.034px;
          height: 16px;
          filter: drop-shadow(0 1px 1px rgba(0,0,0,0.5));
        }

        @media (min-width: 768px) {
          .nav-arrows {
            right: 32px;
            bottom: 32px;
          }
        }

        @media (min-width: 992px) {
          .nav-arrows {
            right: 64px;
            bottom: 64px;
          }
          .nav-arrows img {
            width: 64.551px;
            height: 24px;
          }
        }

        @media (min-width: 768px) {
          .slide-overlay {
            padding-bottom: 26px;
            --gap-ts: 4px;
            --gap-sb: 26px;
          }
          .slide-overlay.left {
            padding-bottom: 0;
            padding-left: 26px;
          }
          .slide-overlay-inner {
            max-width: 400px;
          }

          .slide-title {
            font-size: 84px;
            line-height: 48.54px;
            text-shadow: 0 0 66.19px #000;
          }

          .slide-subtitle {
            font-size: 16px;
            line-height: 24px;
            text-shadow: 0 0 33.095px #000;
            letter-spacing: normal;
            font-weight: 400;
          }

          .slide-button {
            padding: 8.825px 13.238px;
            border-radius: 55.159px;
            border: 0.552px solid #575756;
            font-size: 8.825px;
            letter-spacing: 0.883px;
          }
        }

        @media (min-width: 992px) {
          .slide-overlay {
            padding-bottom: 64px;
            --gap-ts: 8px;
            --gap-sb: 48px;
          }
          .slide-overlay.left {
            padding-bottom: 0;
            padding-left: 64px;
          }
          .slide-overlay-inner {
            max-width: 800px;
          }

          .slide-title {
            font-size: 160px;
            line-height: 120px;
            text-shadow: 0 0 120px #000;
          }

          .slide-subtitle {
            font-size: 28px;
            line-height: normal;
            text-shadow: 0 0 120px #000;
            letter-spacing: 0.42px;
          }

          .slide-button {
            padding: 16px 24px;
            border-radius: 100px;
            border: 1px solid #575756;
            font-size: 16px;
            letter-spacing: 1.6px;
          }
        }
      </style>
      <div
        class="relative w-full rounded-[12px] overflow-hidden aspect-[3/4] md:aspect-[7/4] lg:aspect-[2/1] select-none"
        style="touch-action: pan-y"
      >
        <div
          class="flex h-full transition-transform duration-500 ease-in-out bg-black"
          style=${styleMap(slideContainerStyles)}
          @mousedown=${this._onPointerDown}
          @mousemove=${this._onPointerMove}
          @mouseup=${this._onPointerUp}
          @mouseleave=${this._onPointerUp}
          @touchstart=${this._onPointerDown}
          @touchmove=${this._onPointerMove}
          @touchend=${this._onPointerUp}
        >
          ${map(
            this.items,
            (item, index) => html`
              <div
                class="slide relative flex-shrink-0 w-full h-full overflow-hidden"
                data-index=${index}
              >
                ${this._renderSlideContent(item, index)}
                ${this._renderSlideOverlay(item)}
              </div>
            `
          )}
        </div>

        <div class="nav-arrows">
          <button
            @click=${this._prevSlide}
            aria-label="Slide anterior"
          >
            <img
              src=${leftArrowUrl}
              alt="Anterior"
            />
          </button>
          <button
            @click=${this._nextSlide}
            aria-label="Siguiente slide"
          >
            <img
              src=${rightArrowUrl}
              alt="Siguiente"
            />
          </button>
        </div>
      </div>
    `;
  }
}